import { Instrument } from '../utils/types'
import { UpdateConnectedInstrument } from '../hooks/contexts/connectedInstrumentsContext'
import { MQTT_API } from '../utils/api'
import { HeartbeatMessage, NotificationMessage } from './mqttTopics'

export const generateRandomClientId = () => {
    const randomNumber = Math.floor(Math.random() * 90000) + 10000
    return randomNumber.toString()
}

// Mock instruments data
export const mockConnectedInstruments: Instrument[] = [
    {
        deviceId: 'joystick1',
        serialNr: 'JOY-12345',
        type: 'joysticks',
        batteryLevel: 0,
        volume: 0,
        currentSong: { id: undefined, songType: undefined, sound: undefined, category: undefined, key: undefined },
        arrangement: ''
    },
    {
        deviceId: 'buttons1',
        serialNr: 'BUT-12345',
        type: 'buttons',
        currentSong: { id: undefined, songType: undefined, sound: undefined, category: undefined, key: undefined },
        batteryLevel: 0,
        volume: 0,
        arrangement: ''
    },
    {
        deviceId: 'hybrid1',
        serialNr: 'HYB-12345',
        type: 'hybrid',
        currentSong: { id: undefined, songType: undefined, sound: undefined, category: undefined, key: undefined },
        batteryLevel: 0,
        volume: 0,
        touchMode: 'EASY',
        arrangement: ''
    },
    {
        deviceId: 'touch1',
        serialNr: 'TOU-12345',
        type: 'touch',
        currentSong: { id: undefined, songType: undefined, sound: undefined, category: undefined, key: undefined },
        batteryLevel: 0,
        volume: 0,
        touchMode: 'EASY',
        arrangement: ''
    }
]

export const cleanupOfflineTimers = (offlineTimersRef: React.MutableRefObject<Record<string, number>>, offlineGenRef: React.MutableRefObject<Record<string, number>>) => {
    Object.values(offlineTimersRef.current).forEach((t) => window.clearTimeout(t))
    offlineTimersRef.current = {}
    offlineGenRef.current = {}
}

export const cancelOfflineTimer = (deviceId: string, offlineTimersRef: React.MutableRefObject<Record<string, number>>, offlineGenRef?: React.MutableRefObject<Record<string, number>>) => {
    // Invalidate any pending timer callback (race-proof)
    if (offlineGenRef) {
        offlineGenRef.current[deviceId] = (offlineGenRef.current[deviceId] ?? 0) + 1
    }

    const timer = offlineTimersRef.current[deviceId]
    if (timer) {
        window.clearTimeout(timer)
        delete offlineTimersRef.current[deviceId]
    }
}

export const markPresent = (instrument: Instrument, updateConnectedInstruments: UpdateConnectedInstrument) => {
    updateConnectedInstruments({
        ...instrument
    })
}

export const updateFromHeartbeat = (instrument: Instrument, updateConnectedInstruments: UpdateConnectedInstrument, heartbeatData: HeartbeatMessage) => {
    updateConnectedInstruments({
        ...instrument,
        batteryLevel: heartbeatData.battery.level,
        volume: heartbeatData.volume,
        firmware: heartbeatData.firmware,
        arrangement: heartbeatData.arrangement
    })
}

export const updateFromNotification = (instrument: Instrument, updateConnectedInstruments: UpdateConnectedInstrument, notificationData: NotificationMessage) => {
    if (notificationData.type === 'change' && notificationData.data.what === 'arrangement') {
        updateConnectedInstruments({
            ...instrument,
            arrangement: notificationData.data.value
        })
    }
    // if (notificationData.type === 'change' && notificationData.data.what === 'volume') {
    //     updateConnectedInstruments({
    //         ...instrument,
    //         volume: notificationData.data.value
    //     })
    // }
}

export const markUnknown = (instrument: Instrument, updateConnectedInstruments: UpdateConnectedInstrument) => {
    updateConnectedInstruments({
        ...instrument
    })
}

export const markOffline = (instrument: Instrument, updateConnectedInstruments: UpdateConnectedInstrument) => {
    // Remove from UI when OFFLINE (your requirement)
    updateConnectedInstruments(
        {
            ...instrument
        },
        true
    )
}

export const scheduleOffline = (instrument: Instrument, offlineTimersRef: React.MutableRefObject<Record<string, number>>, updateConnectedInstruments: UpdateConnectedInstrument, OFFLINE_GRACE_MS: number, offlineGenRef?: React.MutableRefObject<Record<string, number>>, onCommittedOffline?: (deviceId: string) => void) => {
    const deviceId = instrument.deviceId

    markUnknown(instrument, updateConnectedInstruments)

    const gen = offlineGenRef ? (offlineGenRef.current[deviceId] ?? 0) + 1 : 0
    if (offlineGenRef) offlineGenRef.current[deviceId] = gen

    const existing = offlineTimersRef.current[deviceId]
    if (existing) window.clearTimeout(existing)

    offlineTimersRef.current[deviceId] = window.setTimeout(() => {
        if (offlineGenRef && offlineGenRef.current[deviceId] !== gen) return

        delete offlineTimersRef.current[deviceId]

        // ✅ device is now considered truly OFFLINE
        onCommittedOffline?.(deviceId)

        markOffline(instrument, updateConnectedInstruments)
    }, OFFLINE_GRACE_MS)
}
